// ===========================================
// Bank Details Tab
// ===========================================
// Form for Australian direct deposit bank account details

import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { Save as SaveIcon, AccountBalance as BankIcon } from '@mui/icons-material';
import { useBankAccount, useUpdateBankAccount } from '../../../hooks/usePricing';

// ===========================================
// HELPERS
// ===========================================

/**
 * Format BSB as XXX-XXX
 */
function formatBsb(value: string): string {
  // Remove non-digits
  const digits = value.replace(/\D/g, '');
  // Limit to 6 digits
  const limited = digits.slice(0, 6);
  // Add dash after 3rd digit
  if (limited.length > 3) {
    return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  }
  return limited;
}

/**
 * Validate BSB (6 digits)
 */
function isValidBsb(bsb: string): boolean {
  const digits = bsb.replace(/\D/g, '');
  return digits.length === 6;
}

/**
 * Validate account number (6-10 digits)
 */
function isValidAccountNumber(accountNumber: string): boolean {
  return /^\d{6,10}$/.test(accountNumber);
}

// ===========================================
// COMPONENT
// ===========================================

export default function BankDetailsTab() {
  const { data: bankAccountData, isLoading, error } = useBankAccount();
  const updateMutation = useUpdateBankAccount();

  const [formData, setFormData] = useState({
    bsb: '',
    accountNumber: '',
    accountName: '',
  });
  const [errors, setErrors] = useState({
    bsb: '',
    accountNumber: '',
    accountName: '',
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form when data loads
  useEffect(() => {
    if (bankAccountData) {
      setFormData({
        bsb: bankAccountData.bsb || '',
        accountNumber: bankAccountData.accountNumber || '',
        accountName: bankAccountData.accountName || '',
      });
      setHasChanges(false);
    }
  }, [bankAccountData]);

  // Handle BSB change with formatting
  const handleBsbChange = (value: string) => {
    const formatted = formatBsb(value);
    setFormData((prev) => ({ ...prev, bsb: formatted }));
    setHasChanges(true);

    // Clear error on change
    if (errors.bsb) {
      setErrors((prev) => ({ ...prev, bsb: '' }));
    }
  };

  // Handle account number change
  const handleAccountNumberChange = (value: string) => {
    // Only allow digits
    const digits = value.replace(/\D/g, '').slice(0, 10);
    setFormData((prev) => ({ ...prev, accountNumber: digits }));
    setHasChanges(true);

    if (errors.accountNumber) {
      setErrors((prev) => ({ ...prev, accountNumber: '' }));
    }
  };

  // Handle account name change
  const handleAccountNameChange = (value: string) => {
    setFormData((prev) => ({ ...prev, accountName: value }));
    setHasChanges(true);

    if (errors.accountName) {
      setErrors((prev) => ({ ...prev, accountName: '' }));
    }
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors = {
      bsb: '',
      accountNumber: '',
      accountName: '',
    };

    if (!formData.bsb) {
      newErrors.bsb = 'BSB is required';
    } else if (!isValidBsb(formData.bsb)) {
      newErrors.bsb = 'BSB must be 6 digits';
    }

    if (!formData.accountNumber) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!isValidAccountNumber(formData.accountNumber)) {
      newErrors.accountNumber = 'Account number must be 6-10 digits';
    }

    if (!formData.accountName.trim()) {
      newErrors.accountName = 'Account name is required';
    }

    setErrors(newErrors);
    return !newErrors.bsb && !newErrors.accountNumber && !newErrors.accountName;
  };

  // Handle save
  const handleSave = async () => {
    if (!validate()) return;

    try {
      await updateMutation.mutateAsync({
        bsb: formData.bsb.replace(/-/g, ''), // Remove dash for API
        accountNumber: formData.accountNumber,
        accountName: formData.accountName.trim(),
      });
      setHasChanges(false);
    } catch {
      // Error handled by mutation
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error">
        Failed to load bank account details. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your bank account details for direct deposit payments. Parents will see
        these details when they choose to pay via bank transfer.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, maxWidth: 500 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <BankIcon color="primary" />
          <Typography variant="h6">Bank Account Details</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="BSB"
            value={formData.bsb}
            onChange={(e) => handleBsbChange(e.target.value)}
            placeholder="XXX-XXX"
            error={!!errors.bsb}
            helperText={errors.bsb || 'Bank State Branch number (6 digits)'}
            fullWidth
            inputProps={{ maxLength: 7 }}
          />

          <TextField
            label="Account Number"
            value={formData.accountNumber}
            onChange={(e) => handleAccountNumberChange(e.target.value)}
            placeholder="123456789"
            error={!!errors.accountNumber}
            helperText={errors.accountNumber || 'Your bank account number (6-10 digits)'}
            fullWidth
            inputProps={{ maxLength: 10 }}
          />

          <TextField
            label="Account Name"
            value={formData.accountName}
            onChange={(e) => handleAccountNameChange(e.target.value)}
            placeholder="Music n Me Pty Ltd"
            error={!!errors.accountName}
            helperText={errors.accountName || 'The name on your bank account'}
            fullWidth
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              startIcon={updateMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {updateMutation.isSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Bank account details saved successfully.
        </Alert>
      )}
    </Box>
  );
}
